import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { getMobileNavItems } from "@/components/MobileBottomNav";
import { useUserRole } from "@/hooks/useUserRole";
import { useViewMode } from "@/hooks/useViewMode";

const SWIPE_MIN_DISTANCE = 80;
const VERTICAL_DOMINANCE = 1.5;
const MAX_SWIPE_MS = 900;
// Swipes that start or end this close to a carousel still count as carousel
// touches, so page navigation never fires when brushing its edge or arrows.
const CAROUSEL_PROXIMITY_PX = 32;

/**
 * Full-page swipe navigation between the bottom-nav tabs (mobile only).
 * A horizontal swipe anywhere on a tab page moves to the next/previous tab.
 * Swipes that begin or end on or near horizontally scrollable content
 * (carousels, filter chip rows) are ignored so those controls keep working.
 */
export const useSwipeTabNavigation = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { role, isAuthenticated, isLoading, approvalStatus } = useUserRole();
  const { viewMode } = useViewMode();

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!window.matchMedia("(pointer: coarse)").matches) return;

    let startX = 0;
    let startY = 0;
    let startT = 0;
    let tracking = false;

    // Carousels (embla) clip with overflow-hidden rather than scroll, so an
    // overflow-hidden box whose content is wider than the box is also treated
    // as a carousel. Tiny clipped boxes (rounded images, chips) are excluded.
    const findHorizontalScroller = (start: Element | null): HTMLElement | null => {
      let node: HTMLElement | null =
        start instanceof HTMLElement ? start : start?.parentElement ?? null;
      while (node && node !== document.body) {
        const style = window.getComputedStyle(node);
        const clipped = style.overflowX === "hidden";
        const scrollable =
          style.overflowX === "auto" || style.overflowX === "scroll";
        if (
          (scrollable || clipped) &&
          node.scrollWidth > node.clientWidth + 4 &&
          node.clientWidth >= 200
        ) {
          return node;
        }
        if (node.classList.contains("snap-x")) return node;
        node = node.parentElement;
      }
      return null;
    };

    const isNearHorizontalScroller = (x: number, y: number, target: Element | null) => {
      const scroller = findHorizontalScroller(target);
      if (!scroller) return false;
      const rect = scroller.getBoundingClientRect();
      return (
        x >= rect.left - CAROUSEL_PROXIMITY_PX &&
        x <= rect.right + CAROUSEL_PROXIMITY_PX &&
        y >= rect.top - CAROUSEL_PROXIMITY_PX &&
        y <= rect.bottom + CAROUSEL_PROXIMITY_PX
      );
    };

    const onTouchStart = (e: TouchEvent) => {
      if (e.touches.length !== 1) {
        tracking = false;
        return;
      }
      const target = e.target as HTMLElement | null;
      const touch = e.touches[0];
      if (
        target &&
        target instanceof Element &&
        (target.closest('[role="dialog"], input, textarea, select') ||
          isNearHorizontalScroller(touch.clientX, touch.clientY, target))
      ) {
        tracking = false;
        return;
      }
      startX = touch.clientX;
      startY = touch.clientY;
      startT = Date.now();
      tracking = true;
    };

    const onTouchEnd = (e: TouchEvent) => {
      if (!tracking || e.changedTouches.length === 0) return;
      tracking = false;
      const touch = e.changedTouches[0];
      const dx = touch.clientX - startX;
      const dy = touch.clientY - startY;
      if (Date.now() - startT > MAX_SWIPE_MS) return;
      if (
        Math.abs(dx) < SWIPE_MIN_DISTANCE ||
        Math.abs(dx) < Math.abs(dy) * VERTICAL_DOMINANCE
      ) {
        return;
      }
      // A swipe that finishes on or beside a carousel belongs to the carousel
      const endTarget = document.elementFromPoint(touch.clientX, touch.clientY);
      if (
        endTarget &&
        isNearHorizontalScroller(touch.clientX, touch.clientY, endTarget)
      ) {
        return;
      }

      // Standalone scanner pages are single-purpose tools, never swipe them
      if (location.pathname.startsWith("/check-in/")) return;

      const items = getMobileNavItems({
        isLoading,
        isAuthenticated,
        role,
        approvalStatus,
        viewMode,
      });
      const currentIndex = items.findIndex((it) =>
        it.path === "/" ? location.pathname === "/" : location.pathname.startsWith(it.path)
      );
      // Only swipe within the tab pages themselves
      if (currentIndex === -1) return;

      const nextIndex = dx < 0 ? currentIndex + 1 : currentIndex - 1;
      if (nextIndex < 0 || nextIndex >= items.length) return;
      const next = items[nextIndex];
      if (next.path === location.pathname) return;
      navigate(next.path);
    };

    document.addEventListener("touchstart", onTouchStart, { passive: true });
    document.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      document.removeEventListener("touchstart", onTouchStart);
      document.removeEventListener("touchend", onTouchEnd);
    };
  }, [location.pathname, isLoading, isAuthenticated, role, approvalStatus, viewMode, navigate]);
};
